import {
	getSavedBandwidthEstimate,
	saveBandwidthEstimateFromProbe
} from '~/composables/useHlsPlayer/shakaLoader';
import {
	getProxiedVideoUrl,
	prefetchFirstSegment,
	resolveFirstSegmentUrl,
	resolveHlsWarmupUrls
} from '~/composables/useVideoUrls';

import { isIOSNativePlayback } from '~/components/PropertyReelsFeed/useIOSSharedVideo';

/**
 * Warm-up helpers for HLS video delivery: SSR head hints (preconnect/preload)
 * and client-side manifest warming. Extracted from the shorts page and
 * ShortsSection so every call site shares the same guards (malformed URLs,
 * Safari's missing requestIdleCallback, Data Saver).
 */

/** Origin of a video URL, or null when the URL is absent/relative/malformed. */
function safeVideoOrigin(url: string | null | undefined): string | null {
	if (!url?.startsWith('http')) return null;
	try {
		return new URL(url).origin;
	} catch {
		return null;
	}
}

/**
 * Emits a `preconnect` head link for the video CDN, derived from an actual
 * video URL so a CDN domain change never leaves a stale hardcode. No-op when
 * the URL is absent or malformed. Must be called during component setup.
 */
export function useVideoCdnPreconnect(
	videoUrl: string | null | undefined
): void {
	const origin = safeVideoOrigin(videoUrl);
	if (!origin) return;
	useHead({
		link: [
			{
				rel: 'preconnect',
				href: origin,
				crossorigin: 'anonymous'
			}
		]
	});
}

/**
 * SSR head hints for the entry video of a shorts page: preconnect + preload of
 * the full HLS chain (master → lowest variant playlist → first segment), so
 * the video downloads in parallel with the JS bundle instead of after
 * hydration.
 *
 * - Server-only resolution (`resolveHlsWarmupUrls`: memory + Redis cache,
 *   800ms timeout). On failure the page renders without hints — the player's
 *   own fetch flow is untouched.
 * - The master preload URL must match the player's request exactly
 *   (getProxiedVideoUrl appends `_cb=1`); variant/segment URLs carry only
 *   `v=HASH`, matching Shaka's scheme handler.
 * - The segment preload uses fetchpriority=low so video bytes never compete
 *   with the LCP element.
 *
 * Must be awaited during component setup (uses useAsyncData/useHead).
 */
export async function useShortsHlsWarmup(
	rawMasterUrl: string | null | undefined,
	asyncDataKey: string
): Promise<void> {
	const proxiedMasterUrl = getProxiedVideoUrl(rawMasterUrl);
	const cdnOrigin = safeVideoOrigin(proxiedMasterUrl);
	if (!proxiedMasterUrl || !cdnOrigin) return;

	useHead({
		link: [
			{
				rel: 'preconnect',
				href: cdnOrigin,
				crossorigin: 'anonymous'
			},
			{
				rel: 'preload',
				as: 'fetch',
				href: proxiedMasterUrl,
				crossorigin: 'anonymous'
			}
		]
	});

	const { data: warmup } = await useAsyncData(
		`shorts-hls-warmup:${asyncDataKey}`,
		async () => {
			if (!import.meta.server || !rawMasterUrl?.startsWith('http')) {
				return null;
			}
			return resolveHlsWarmupUrls(rawMasterUrl, AbortSignal.timeout(800));
		},
		{ default: () => null }
	);

	if (!warmup.value) return;

	// The segment preload is UNIVERSAL — no per-request gate. Two reasons:
	//
	// 1. This HTML is cached by CloudFront with `Vary: Accept-Encoding` alone,
	//    so any per-request decision here (the previous Save-Data check, or a
	//    network client hint) leaks: whoever populates the cache entry fixes
	//    the preload set for every visitor until the TTL expires. A gate that
	//    fires at random is worse than no gate.
	// 2. This is the entry video of its own page — playback is near-certain,
	//    so the segment bytes are needed either way. Starting them at HTML
	//    parse beats starting after hydration or after the tap, especially on
	//    slow networks, and fetchpriority=low keeps them out of the LCP's way.
	//    The cost is borne only by visitors who bounce without playing.
	//
	// Speculative contexts (carousels) DO gate — see useCarouselSegmentWarmup.
	useHead({
		link: [
			...(warmup.value.variantPlaylistUrl
				? [
						{
							rel: 'preload',
							as: 'fetch' as const,
							href: warmup.value.variantPlaylistUrl,
							crossorigin: 'anonymous' as const
						}
					]
				: []),
			...(warmup.value.firstSegmentUrl
				? [
						{
							rel: 'preload',
							as: 'fetch' as const,
							href: warmup.value.firstSegmentUrl,
							crossorigin: 'anonymous' as const,
							// Video data must not compete with the LCP element (the
							// full-screen poster) — download in leftover bandwidth.
							fetchpriority: 'low' as const
						}
					]
				: [])
		]
	});
}

// Ceiling for SPECULATIVE segment warm-ups (visible carousels): only variants
// at or below this declared bandwidth — the same ceiling the intent prefetch
// uses on slow networks (INTENT_MAX_SEGMENT_BPS_ON_SLOW). At ~2.5 Mbps a
// segment is a few hundred KB; above it, speculation costs more than it saves.
// The entry-video preload is NOT capped by this: there, playback is certain.
const SPECULATIVE_SEGMENT_MAX_BPS = 2_500_000;

// The Shaka chunk only needs to warm once per session — the browser HTTP
// cache and Vite's module cache make repeats free, but the idle callback
// bookkeeping isn't.
let shakaChunkWarmed = false;

/**
 * Warms the Shaka player chunk (~764 KB) from pages that can navigate into
 * shorts (home/SRP/PDP sections), so the first play skips the biggest serial
 * download. Import-only — no polyfills or cache init run until the player
 * actually mounts and calls loadShaka().
 *
 * Deliberately zero-impact on the hosting page: client-only, once per
 * session, skipped under Data Saver and on iOS native playback (which never
 * loads Shaka), and deferred to idle time so it never competes with
 * hydration, LCP or user input. Never throws.
 */
export function prefetchShakaChunkOnIdle(): void {
	if (!import.meta.client || shakaChunkWarmed) return;

	try {
		const connection = (
			navigator as Navigator & { connection?: { saveData?: boolean } }
		).connection;
		if (connection?.saveData) return;
		if (isIOSNativePlayback()) return;

		shakaChunkWarmed = true;
		const run = () => {
			import('shaka-player').catch(() => {
				shakaChunkWarmed = false;
			});
		};

		if ('requestIdleCallback' in window) {
			requestIdleCallback(run, { timeout: 10_000 });
		} else {
			setTimeout(run, 2000);
		}
	} catch {
		// Warm-up is best-effort — never propagate into the caller.
	}
}

// Videos already prefetched by intent this session — a click or hover repeat
// must not re-download. Capped so an infinite-scroll session can't grow it.
const intentPrefetched = new Set<string>();
const INTENT_PREFETCH_MAX_ENTRIES = 300;
// On slow networks, only pull first segments of variants at or below this
// declared bandwidth — an old single-variant original (4-9 Mbps) would hog
// the thin pipe right when the feed is about to mount and need it.
const INTENT_MAX_SEGMENT_BPS_ON_SLOW = 2_500_000;
const FAST_NETWORK_BPS = 4_000_000;

function rememberIntent(url: string): boolean {
	if (intentPrefetched.has(url)) return false;
	if (intentPrefetched.size >= INTENT_PREFETCH_MAX_ENTRIES) {
		const oldest = intentPrefetched.values().next().value;
		if (oldest !== undefined) intentPrefetched.delete(oldest);
	}
	intentPrefetched.add(url);
	return true;
}

/**
 * Whether this video's chain was already prefetched by user intent in this
 * session — lets QoE telemetry separate warmed plays from cold plays.
 * Accepts raw or already-proxied URLs: re-proxying an already-proxied URL
 * appends the cache-bust param twice and would never match the stored key.
 */
export function wasVideoIntentPrefetched(
	videoUrl: string | null | undefined
): boolean {
	if (!videoUrl) return false;
	if (intentPrefetched.has(videoUrl)) return true;
	const url = getProxiedVideoUrl(videoUrl);
	return url ? intentPrefetched.has(url) : false;
}

/**
 * Strong-intent prefetch: the user clicked something that navigates to this
 * video (feed card, cluster card) OR swiped an autoplaying carousel toward it
 * (the next active card plays by itself). Downloading its chain is not
 * speculative — the player will request these exact bytes moments from now.
 * Pulls master + variant playlist + first segment into the CacheStorage the
 * player reads from.
 *
 * On slow networks the segment is capped by declared bandwidth so a heavy
 * legacy variant doesn't hog the pipe the feed is about to need; manifests
 * always warm. Fire-and-forget — never throws, never blocks navigation.
 */
export function prefetchVideoOnIntent(
	videoUrl: string | null | undefined
): void {
	if (!import.meta.client || !videoUrl) return;

	const url = getProxiedVideoUrl(videoUrl);
	if (!url || !rememberIntent(url)) return;

	try {
		const bps = getSavedBandwidthEstimate();
		// This prefetch only fires on the click that opens the feed on this
		// exact video — playback is certain, and on legacy single-variant
		// videos the player would download the very same heavy segment moments
		// later anyway. Skipping here would only lose the parallelism with the
		// navigation, so an unknown estimate (first visit) does not cap.
		const networkIsFast = bps === null || bps >= FAST_NETWORK_BPS;
		void prefetchFirstSegment(
			url,
			new AbortController().signal,
			'lowest',
			networkIsFast ? undefined : INTENT_MAX_SEGMENT_BPS_ON_SLOW
		).catch(() => {});
	} catch {
		// Best-effort — never let a prefetch failure affect the click.
	}
}

/**
 * Speculative hover warm: the user is pointing at a card but has not clicked.
 * Only the master manifest (~400 B) is fetched — enough to remove a round
 * trip from hover-to-play and from a subsequent click, cheap enough to fire
 * on every card the pointer crosses.
 */
export function warmVideoManifestOnHover(
	videoUrl: string | null | undefined
): void {
	if (!import.meta.client || !videoUrl) return;
	const url = getProxiedVideoUrl(videoUrl);
	if (!url || intentPrefetched.has(url)) return;
	try {
		fetch(url, { priority: 'low' } as RequestInit).catch(() => {});
	} catch {
		// Best-effort.
	}
}

/**
 * Warms the HLS master manifests (~400 B each) of the first videos in a list
 * so that when a card autoplays on scroll or hover, the player skips the
 * manifest round-trip. Manifest-only — segments are too heavy to speculate on
 * from a carousel.
 *
 * Guards: client-only, skipped under Data Saver, deferred to idle time
 * (setTimeout fallback for Safari, which lacks requestIdleCallback), and
 * never throws — a warm failure must not break the caller's data flow.
 */
export function warmVideoManifests(
	videoUrls: Array<string | null | undefined>,
	count: number = 2
): void {
	if (!import.meta.client) return;

	try {
		const connection = (
			navigator as Navigator & { connection?: { saveData?: boolean } }
		).connection;
		if (connection?.saveData) return;

		const run = () => {
			videoUrls
				.filter((url): url is string => url != null)
				.slice(0, count)
				.forEach(rawUrl => {
					const url = getProxiedVideoUrl(rawUrl);
					if (!url || intentPrefetched.has(url)) return;
					fetch(url, { priority: 'low' } as RequestInit).catch(() => {});
				});
		};

		if ('requestIdleCallback' in window) {
			requestIdleCallback(run);
		} else {
			setTimeout(run, 200);
		}
	} catch {
		// Warm-up is best-effort — never propagate into the caller.
	}
}

// Speculative segment warm-up for visible carousels: how many cards deep to
// go, and how long any single warm may run. The user browses left-to-right,
// so the first cards are the likeliest taps.
const CAROUSEL_SEGMENT_WARM_COUNT = 3;
const CAROUSEL_SEGMENT_WARM_TIMEOUT_MS = 10_000;

/**
 * Lazily pulls the first segment of a visible carousel's first videos while
 * the user browses, so a tap starts playback from CacheStorage instead of the
 * network. Sits between the manifest warm (data arrival / hover) and the
 * confirmed-intent prefetch (click) — it downloads real video bytes, so every
 * guard is stacked:
 *
 * - hover-capable pointers only (desktop): there, hovering a card PLAYS it
 *   (VideoCard's hoverToPlay), so the warmed segment has a real consumer. On
 *   touch devices no hover exists — a tap navigates to the feed, where the
 *   click prefetch plus the entry page's own SSR preload already cover the
 *   chain, so a carousel warm would be speculative bytes with no consumer;
 * - only after the section is actually visible (caller's IntersectionObserver);
 * - only when this user's MEASURED bandwidth is fast (no estimate = no bytes —
 *   speculating on an unknown, possibly metered pipe is how the SSR segment
 *   preload went wrong);
 * - only light variants (SPECULATIVE_SEGMENT_MAX_BPS cap → a few hundred KB);
 * - one video at a time, each queued behind an idle callback, so the queue
 *   never competes with hydration, LCP or user input;
 * - deduped against every other warm layer via the shared intent set;
 * - skipped under Data Saver, on iOS native playback (which never reads the
 *   Shaka cache) and in hidden tabs; abandoned when the caller's scope dies.
 *
 * Call during component setup; re-fires when visibility or the list changes.
 */
export function useCarouselSegmentWarmup(
	videoUrls: () => Array<string | null | undefined>,
	isVisible: () => boolean
): void {
	if (!import.meta.client) return;

	let disposed = false;
	onScopeDispose(() => {
		disposed = true;
	});

	const idle = (run: () => void): void => {
		if ('requestIdleCallback' in window) {
			requestIdleCallback(run, { timeout: 10_000 });
		} else {
			setTimeout(run, 1000);
		}
	};

	const warmQueue = (urls: string[]): void => {
		const [next, ...rest] = urls;
		if (!next || disposed) return;
		idle(() => {
			if (disposed || document.hidden) return;
			if (!rememberIntent(next)) {
				warmQueue(rest);
				return;
			}
			void prefetchFirstSegment(
				next,
				AbortSignal.timeout(CAROUSEL_SEGMENT_WARM_TIMEOUT_MS),
				'lowest',
				SPECULATIVE_SEGMENT_MAX_BPS
			)
				.catch(() => {})
				.finally(() => warmQueue(rest));
		});
	};

	const attempt = (): void => {
		const visible = isVisible();
		const urls = videoUrls();
		if (disposed || !visible || urls.length === 0) return;
		try {
			// No hover, no consumer: touch devices only fetch on tap.
			if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches)
				return;
			const connection = (
				navigator as Navigator & { connection?: { saveData?: boolean } }
			).connection;
			if (connection?.saveData) return;
			if (isIOSNativePlayback()) return;
			const bps = getSavedBandwidthEstimate();
			if (bps === null) {
				probeBandwidthForCarousel(urls, attempt);
				return;
			}
			if (bps < FAST_NETWORK_BPS) return;

			const proxied = urls
				.filter((url): url is string => url != null)
				.slice(0, CAROUSEL_SEGMENT_WARM_COUNT)
				.map(url => getProxiedVideoUrl(url))
				.filter((url): url is string => url !== null)
				.filter(url => !intentPrefetched.has(url));
			warmQueue(proxied);
		} catch {
			// Warm-up is best-effort — never propagate into the caller.
		}
	};

	watch([isVisible, videoUrls] as const, () => attempt(), { immediate: true });
}

let carouselProbeState: 'idle' | 'running' | 'done' = 'idle';

function probeBandwidthForCarousel(
	urls: Array<string | null | undefined>,
	onDone: () => void
): void {
	if (carouselProbeState !== 'idle') return;
	const firstUrl = urls.find((url): url is string => url != null);
	const proxied = firstUrl ? getProxiedVideoUrl(firstUrl) : null;
	if (!proxied) return;
	carouselProbeState = 'running';

	const run = (): void => {
		void (async () => {
			try {
				const segmentUrl = await resolveFirstSegmentUrl(
					proxied,
					AbortSignal.timeout(8_000)
				);
				if (segmentUrl) {
					const probeUrl = new URL(segmentUrl);
					probeUrl.searchParams.set('_cb', '1');
					await saveBandwidthEstimateFromProbe(
						probeUrl.toString(),
						AbortSignal.timeout(8_000)
					);
				}
			} catch {
				// noop
			} finally {
				carouselProbeState = 'done';
				onDone();
			}
		})();
	};

	if ('requestIdleCallback' in window) {
		requestIdleCallback(run, { timeout: 5_000 });
	} else {
		setTimeout(run, 1_500);
	}
}
